import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {useCallback, useMemo, useState} from 'react'
import {getEntityCompositeID, sortFindingsBySeverity} from './entity-helpers'
import type {FindingEntity} from './types'

const dismissFinding = async (basePath: string, finding: FindingEntity): Promise<Response> => {
  return verifiedFetchJSON(`${basePath}/code_review_findings/ignore`, {
    method: 'POST',
    body: {entity_type: finding.entityType, entity_id: finding.entity.id},
  })
}

const reopenFinding = async (basePath: string, finding: FindingEntity): Promise<Response> => {
  return verifiedFetchJSON(`${basePath}/code_review_findings/ignore`, {
    method: 'DELETE',
    body: {entity_type: finding.entityType, entity_id: finding.entity.id},
  })
}

export function useFindingDismissals(
  findings: FindingEntity[],
  pullRequestBasePath: string,
  dismissedEntityIds: string[],
) {
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(() => new Set(dismissedEntityIds))

  const handleDismiss = useCallback(
    async (id: string) => {
      setDismissedIds(prev => new Set(prev).add(id))
      const finding = findings.find(f => getEntityCompositeID(f) === id)
      if (finding) {
        try {
          await dismissFinding(pullRequestBasePath, finding)
        } catch {
          setDismissedIds(prev => {
            const next = new Set(prev)
            next.delete(id)
            return next
          })
        }
      }
    },
    [findings, pullRequestBasePath],
  )

  const handleReopen = useCallback(
    async (id: string) => {
      setDismissedIds(prev => {
        const next = new Set(prev)
        next.delete(id)
        return next
      })
      const finding = findings.find(f => getEntityCompositeID(f) === id)
      if (finding) {
        try {
          await reopenFinding(pullRequestBasePath, finding)
        } catch {
          setDismissedIds(prev => new Set(prev).add(id))
        }
      }
    },
    [findings, pullRequestBasePath],
  )

  const openFindings = useMemo(
    () => sortFindingsBySeverity(findings.filter(f => !dismissedIds.has(getEntityCompositeID(f)))),
    [findings, dismissedIds],
  )
  const resolvedFindings = useMemo(
    () => sortFindingsBySeverity(findings.filter(f => dismissedIds.has(getEntityCompositeID(f)))),
    [findings, dismissedIds],
  )

  return {openFindings, resolvedFindings, handleDismiss, handleReopen}
}
